"use client"
import Link from "next/link";
import { useMemo } from "react";
import { BadgeCheck, ChevronLeft, Timer, Info, BellRing, BellOff, Trash2 } from "lucide-react";
import { toast_configs } from "@core/constants/toast_configs";
import { show_mutation_error_toast } from "@core/helpers/mutation-errors";
import MediaComponent from "@/components/common/media-component";
import useProduct from "@core/helpers/client/hooks/useProduct";
import PriceComponent from "@/components/ui/Price";
import useCountDown from "@core/helpers/client/hooks/useCountDown";
import Counter from "@/components/Counter";
import Button from "@/components/ui/button";
import OfferLabels from "@/components/OfferLabels";
import { BrandLabel } from "../SingleProduct/_brandLabel";
import { ProductLabelGuarantee } from "../SingleProduct/_productLabel";
import LoadingCounter from "@/components/LoadingCounter";
import PresaleLeadTime from "@/components/PresaleLeadTime";
import usePresaleCart from "@/hooks/usePresaleCart";
import { as_presale_variant } from "@/helpers/presale";
import { cart_row_key } from "@core/helpers/cart-rows";

const CartItem = ({ item: cart_item, index, variant = "cart", className }) => {
	// ردیف پیش‌فروش موجودیِ صفر دارد و هسته آن را «ناموجود» می‌بیند؛ تعداد
	// باقی‌مانده‌ی پیش‌فروش جای موجودی می‌نشیند (سقف شمارنده و قیمت درست شوند).
	const { rows: presale_rows } = usePresaleCart();
	const presale_row = presale_rows[cart_row_key(cart_item)];
	const is_presale = presale_row?.is_presale === true;
	const item = useMemo(() => {
		if (!is_presale || !cart_item?.product?.mix_variant?.length) return cart_item;
		const [first, ...rest] = cart_item.product.mix_variant;
		// سرور خودش تعداد را به باقی‌مانده کوتاه می‌کند؛ تعداد فعلیِ ردیف همیشه مجاز است
		const remaining = Math.max(Number(presale_row?.remaining) || 0, cart_item?.count || 0);
		return {
			...cart_item,
			product: {
				...cart_item.product,
				mix_variant: [as_presale_variant(first, remaining), ...rest],
			},
		};
	}, [cart_item, is_presale, presale_row?.remaining]);
	const { mix_variant } = item?.product || {};
	const isModal = variant === "modal";
	const isCartPage = variant === "cart";

	const {
		item_count_in_cart,
		remove_from_cart_handler,
		mix_variant_limit_min,
		mix_variant_limit_max,
		change_count_items_in_cart_handler,
		add_to_cart_loading,
		remove_from_cart_loading,
		check_product_availability,
		check_product_offer,
		add_product_to_notifications_handler,
		is_notify,
	} = useProduct({
		...(item || {}),
		...item?.product,
		id: item?.product_id,
	});

	const isAvailable = check_product_availability(mix_variant?.[0]);
	const hasChanges =
		item?.count_history?.length > 0 ||
		(item?.first_price > 0 &&
			mix_variant?.[0]?.price?.main_price > 0 &&
			mix_variant?.[0]?.price?.main_price !== item?.first_price);

	const offerLabel = mix_variant?.[0]?.custom_fields?.custom_offer_label;
	const showRemainProduct =
		mix_variant[0]?.details?.count < 4 ||
		(mix_variant[0]?.details?.limit_max
			? mix_variant[0]?.details?.limit_max < 4
			: false);

	const RemainProduct = () => (
		check_product_availability(mix_variant?.[0]) &&
		showRemainProduct && (
			<div className="flex items-center gap-1 text-xs font-bold text-jmt-red-700">
				<p className="flex items-center gap-1">
					<span>تنها</span>
					{mix_variant[0]?.details?.count < 4
						? mix_variant[0]?.details?.count
						: mix_variant[0]?.details?.limit_max}
					<span>عدد باقی مانده</span>
				</p>
			</div>
		)
	);

	const change_count_handler = (count) => change_count_items_in_cart_handler({
		success_callback: (data) => {
		},
		error_callback: show_mutation_error_toast,
		mix_variant: {
			...mix_variant?.[0],
			keys: item?.mix_variant_keys,
		},
		count: parseInt(count),
	});

	const delete_from_cart_handler = (e) => remove_from_cart_handler(e, {
		success_callback: (data) =>
			toast_configs("کالا از سبد خرید حذف شد", "success", {
				className: "custome_toastify",
				position: "bottom-center",
				autoClose: 4000,
			}),
		error_callback: show_mutation_error_toast,
		mix_variant: {
			...mix_variant?.[0],
			keys: item?.mix_variant_keys,
		},
	});

	const add_to_notify = (e) =>
		add_product_to_notifications_handler(e, {
			mix_variant: mix_variant?.[0],
			success_callback: (data) =>
				toast_configs("عملیات با موفقیت انجام شد", "success", {
					className: "toast-general-style",
				}),
			error_callback: show_mutation_error_toast,
		});

	const timer = useMemo(() => {
		const expire_at = parseInt(mix_variant?.[0].price?.offer_expireAt);
		const start_at = parseInt(mix_variant?.[0].price?.offer_startAt);
		const now_time = new Date().getTime();

		if (
			mix_variant?.[0].price?.show_discount_timer &&
			expire_at > 0 &&
			start_at > 0
		) {
			return expire_at - now_time;
		}

		return undefined;
	}, [item]);

	const total_time = parseInt(mix_variant?.[0].price?.offer_expireAt) -
		parseInt(mix_variant?.[0].price?.offer_startAt);

	const remain_time = timer;

	const timer_percent = total_time > 0 && remain_time > 0
		? (1 - remain_time / total_time) * 100
		: 0;

	const { counter_down, convert_countdown_time } = useCountDown({
		number: timer ? Math.floor(timer / 1000) : 0,
		countdown_className:
			"dir-ltr whitespace-nowrap h-[18px] lg:h-[22px] text-sm font-bold text-jmt-red-700",
	});

	return (
		<>

			<div className={isModal ? "block" : "block lg:hidden"}>
				{/* OfferLabels & timer */}
				<div className={`flex items-center ${timer && offerLabel ? "justify-between" : timer && !offerLabel ? "justify-end" : "justify-start"} ${isCartPage && "py-3"} ${className}`}>

					<div className={`${isAvailable ? "" : "opacity-70"}`}>
						<OfferLabels offerLabel={offerLabel} size="lg" />
					</div>

					{timer > 0 && (
						<>
							{convert_countdown_time(counter_down, {
								formats: ["hours", "minutes", "seconds"],
								convertDaysToHours: true
							})}
						</>
					)}
				</div>

				{/* progressbar */}
				{timer > 0 && isCartPage && (
					<div className="flex items-center justify-between pb-3 gap-2">
						<span className="text-xs text-jmt-red-700">
							{timer_percent?.toFixed()}٪ فروش رفته
						</span>

						{/*progressbar*/}
						<div className="flex-1 h-1 bg-jmt-neutral-200 rounded-full overflow-hidden dir-ltr">
							<div className="h-full bg-jmt-red-700 rounded-full transition-all duration-200"
								style={{ width: `${timer_percent.toFixed()}%` }}
							/>
						</div>
					</div>)}

				<div className="flex items-start justify-between gap-2">

					{/*image */}
					<Link href={item?.product?.seo?.full_url || item?.product?.seo?.url} className={`flex items-center w-[110px] ${!isAvailable ? "opacity-70" : ""}`}>
						<div className="w-[110px] aspect-square overflow-hidden rounded-md relative">
							<MediaComponent
								media={item?.product?.mix_variant?.[0]?.media || item?.product?.media}
								alt={item?.product?.title}
								style={{ width: "100%", height: "100%", objectFit: 'cover', }}
							/>
						</div>
					</Link>

					{/* info */}
					<div className="w-full flex flex-col justify-between gap-2">

						{/*brand*/}
						<div className={`flex items-center gap-2 ${!isAvailable ? "opacity-70" : ""}`}>
							<span className={`text-xs font-bold ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-main "}`}>{item?.product?.brand?.title || 'برند نامشخص'}</span>
							<BrandLabel brand={item?.product?.brand} />

						</div>

						{/* title */}
						<Link href={item?.product?.seo?.full_url || item?.product?.seo?.url || "#"}>
							<p className={`text-sm font-bold line-clamp-2 ${isAvailable ? 'text-jmt-main' : 'text-jmt-neutral-500 opacity-70'}`}>
								{item?.product?.title}
							</p>
						</Link>

						{/*mix variants*/}
						<div className={`flex items-center gap-2 text-xs mt-1 ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}>
							<div className="flex items-center gap-1">
								<div
									className="w-4 h-4 rounded-full border border-neutral-300"
									style={{
										background: `${check_product_availability(mix_variant?.[0]) ?
											item?.product?.mix_variant[0]?.labels_obj?.colors?.value?.color : '#737373'
											}`
									}}
								/>
								<p>رنگ</p>
								<p>{item?.product?.mix_variant[0]?.labels_obj?.colors?.title || 'نامشخص'}</p>
							</div>
							|
							<div className="flex items-center gap-1">
								<p>سایز</p>
								<p>{item?.product?.mix_variant[0]?.labels_obj?.sizes?.title || 'نامشخص'}</p>
							</div>
						</div>

						{/*original or fake product*/}
						{isCartPage &&
							<>
								<div className={`flex items-center gap-1 text-xs ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}>
									<ProductLabelGuarantee noneOrginal={item?.product?.custom_fields?.non_original_product} className="w-4.5! h-4.5!" textClassName="font-normal!" />
								</div>

								{/*shipping time - زمان آماده‌سازی (فقط پیش‌فروش) */}
								{is_presale && (
									<PresaleLeadTime
										hours={presale_row?.lead_hours}
										label="زمان آماده سازی محصول"
										icon={<Timer className="w-4 h-4 stroke-jmt-neutral-700" />}
										className={`text-xs ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}
									/>
								)}
							</>}

						{/* changes */}
						{(isCartPage || isModal) && check_product_availability(mix_variant?.[0]) && (
							<>
								{/* count changes */}
								{item?.count_history?.length > 0 &&
									item?.count_history?.map((count, index) => {
										const different_count =
											index === item?.count_history?.length - 1
												? item?.count - count?.old_count
												: item?.count_history[index + 1]?.old_count - count?.old_count;

										if (different_count < 0) {
											return (
												<div
													key={`count-change-${index}`}
													className="flex items-start gap-1 text-xs text-jmt-yellow-600"
												>
													<Info size={16} className="shrink-0 text-jmt-yellow-600" />
													<span>تغییر تعداد انتخاب شده به دلیل کاهش موجودی انبار</span>
												</div>
											);
										}

										return null;
									})}

								{/* price changes up */}
								{item?.first_price > 0 &&
									mix_variant?.[0]?.price?.main_price > 0 &&
									mix_variant?.[0]?.price?.main_price > item?.first_price && (
										<div className="flex items-start gap-1 text-xs">
											<Info size={16} className="shrink-0 text-jmt-orange-600" />
											<div className="text-jmt-orange-600">
												<span>قیمت از</span>
												<PriceComponent
													price={item?.first_price}
													className="inline-block mx-px"
												/>
												<span>به </span>
												<PriceComponent
													price={mix_variant?.[0]?.price?.main_price}
													className="inline-block mx-px"
												/>
												<span>افزایش یافت</span>
											</div>
										</div>
									)}

								{/* price changes down */}
								{item?.first_price > 0 &&
									mix_variant?.[0]?.price?.main_price > 0 &&
									mix_variant?.[0]?.price?.main_price < item?.first_price && (
										<div className="flex items-start gap-1 text-xs">
											<Info size={16} className="shrink-0 text-jmt-green-600" />
											<div className="text-jmt-green-600">
												<span>قیمت از</span>
												<PriceComponent
													price={item?.first_price}
													className="inline-block mx-px"
												/>
												<span>به </span>
												<PriceComponent
													price={mix_variant?.[0]?.price?.main_price}
													className="inline-block mx-px"
												/>
												<span>کاهش یافت</span>
											</div>
										</div>
									)}
							</>
						)}
					</div>
				</div>

				{/* next cart & RemainProduct */}
				<div className={`flex items-center ${!isAvailable ? "justify-start py-2 gap-2" : "justify-between py-3"}`}>
					{!isAvailable && !isModal && (
						<div className="flex items-center gap-2">
							<button onClick={delete_from_cart_handler}
								className="flex items-center gap-1 text-jmt-neutral-800 text-xs font-bold cursor-pointer">
								{remove_from_cart_loading?.[item?.mix_variant_keys] ? <LoadingCounter className="h-[5px]!" />
									: <>
										<Trash2 size={16} />
										<span>  حذف</span>
									</>}
							</button>

						</div>
					)}

					{/* «سبد خرید بعدی» فعلا غیرفعال است
					{isCartPage && (
						<Button
							end_icon={<ChevronLeft size={16} />}
							color_type="tertiary"
							variant="tertiary"
							text=" بعدا میخرم "
						/>
					)}
					*/}

					<RemainProduct />
				</div>


				{/*price & counter*/}
				<div className="flex items-center justify-between pb-3">

					{/*counter*/}
					{check_product_availability(mix_variant?.[0]) ?
						<div className="[&>.counter-component>.link-to-cart]:hidden">
							<Counter
								min={mix_variant_limit_min({
									mix_variant: {
										...mix_variant?.[0],
										keys: item?.mix_variant_keys,
									},
								})}
								max={mix_variant_limit_max({
									mix_variant: {
										...mix_variant?.[0],
										keys: item?.mix_variant_keys,
									},
								})}
								removeHandler={delete_from_cart_handler}
								quantity={item_count_in_cart({
									mix_variant: {
										...mix_variant?.[0],
										keys: item?.mix_variant_keys,
									},
								})}
								onChange={change_count_handler}
								loading={add_to_cart_loading?.[item?.mix_variant_keys]}
								remove_from_cart_loading={remove_from_cart_loading[item?.mix_variant_keys]}
								isOutOfStock={!check_product_availability(mix_variant?.[0])}
								size="sm"
							/>
						</div>
						: isModal ?
							<div className="[&>.counter-component>.link-to-cart]:hidden">
								<Counter
									min={mix_variant_limit_min({
										mix_variant: {
											...mix_variant?.[0],
											keys: item?.mix_variant_keys,
										},
									})}
									max={mix_variant_limit_max({
										mix_variant: {
											...mix_variant?.[0],
											keys: item?.mix_variant_keys,
										},
									})}
									removeHandler={delete_from_cart_handler}
									quantity={item_count_in_cart({
										mix_variant: {
											...mix_variant?.[0],
											keys: item?.mix_variant_keys,
										},
									})}
									onChange={change_count_handler}
									loading={add_to_cart_loading?.[item?.mix_variant_keys]}
									remove_from_cart_loading={remove_from_cart_loading[item?.mix_variant_keys]}
									isOutOfStock={!check_product_availability(mix_variant?.[0])}
									size="sm"
								/>
							</div>
							: <Button
								start_icon={is_notify?.[mix_variant?.[0]?.keys] ? <BellOff size={20} /> : <BellRing size={20} />}
								text={is_notify?.[mix_variant?.[0]?.keys] ? " دیگه لازم نیست خبرم کنید " : " موجود شد خبرم کنید "}
								onClick={(e) => add_to_notify(e)}
								color_type={is_notify?.[mix_variant?.[0]?.keys] ? "secondary" : "primary"}
								className="whitespace-nowrap w-1/2" />
					}

					{/*price*/}
					{check_product_availability(mix_variant?.[0]) ? (
						<div className="flex flex-col items-end gap-1">

							{/* offer section */}
							{check_product_offer(mix_variant?.[0]) && (
								<div className="flex items-center gap-1">
									<PriceComponent
										price={mix_variant?.[0]?.price?.price * item?.count}
										className="text-jmt-neutral-400 line-through text-sm [&>.price-currency]:hidden"
									/>

									{mix_variant?.[0]?.price?.discount_percent && (
										<div className="flex items-center justify-center bg-jmt-red-700 rounded-md px-1 py-px">
											<span className="text-jmt-white text-xs font-bold">
												{mix_variant?.[0]?.price?.discount_percent}٪
											</span>
										</div>
									)}
								</div>
							)}

							{/* final price */}
							<div className="flex items-center space-x-6">
								<PriceComponent
									price={mix_variant?.[0]?.price?.main_price * item?.count}
									className="font-bold text-lg text-neutral-900"
								/>
							</div>

						</div>
					) : (
						<div className="font-bold text-jmt-neutral-500">
							ناموجود
						</div>
					)}
				</div>
			</div>

			<div className={isModal ? "hidden" : "hidden lg:block"}>
				<div className="py-2">

					{/* progresbar & timer & offer lebel */}
					<div className={`flex items-center justify-start space-x-6 ${!isAvailable ? "opacity-70" : ""}`}>

						{offerLabel &&
							<OfferLabels offerLabel={offerLabel} size="lg" className="pb-3" />}

						{timer > 0 && (
							<div className="flex items-center gap-2 pb-3">
								<span className="text-xs text-jmt-red-700">
									{timer_percent?.toFixed()}٪ فروش رفته
								</span>

								{/*progressbar*/}
								<div className="w-[100px] h-1 bg-jmt-neutral-200 rounded-full overflow-hidden dir-ltr">
									<div className="h-full bg-jmt-red-700 rounded-full transition-all duration-200"
										style={{ width: `${timer_percent.toFixed()}%` }}
									/>
								</div>
								<span className="text-jmt-neutral-200">|</span>

							</div>)}

						{timer > 0 && (
							<div className="flex items-center pb-3">

								{convert_countdown_time(counter_down, {
									formats: ["hours", "minutes", "seconds"],
									convertDaysToHours: true
								})}

							</div>
						)}
					</div>

					<div className="flex flex-wrap sm:flex-nowrap gap-2">

						{/*image*/}
						<Link href={item?.product?.seo?.full_url || item?.product?.seo?.url} className={`flex flex-col items-center w-[162px] ${!isAvailable ? "grayscale opacity-70" : ""}`}>
							<div className="w-[140px] aspect-square overflow-hidden rounded-md relative">
								<MediaComponent
									media={item?.product?.mix_variant?.[0]?.media || item?.product?.media}
									alt={item?.product?.title}
									style={{
										width: "100%",
										height: "100%",
										objectFit: 'cover',
									}}
								/>
							</div>
						</Link>

						{/*info*/}
						<div className="w-full flex flex-col justify-between h-auto gap-2">

							{/*brand*/}
							<div className="flex items-center justify-between gap-2">
								<div className={`flex items-center gap-2 ${!isAvailable ? "opacity-70" : ""}`}>
									<span
										className={`text-sm font-bold ${!isAvailable
											? "text-jmt-neutral-500 opacity-70"
											: "text-jmt-main"
											}`}
									>
										{item?.product?.brand?.title || "برند نامشخص"}
									</span>

									<BrandLabel brand={item?.product?.brand} />
								</div>
								<div className="flex items-center gap-2">
									{!isAvailable && (
										<div className="flex items-center gap-2">

											<button onClick={delete_from_cart_handler}
												className="flex items-center gap-1 text-jmt-neutral-800 text-xs font-bold cursor-pointer">
												{remove_from_cart_loading?.[item?.mix_variant_keys] ? <LoadingCounter className="h-[5px]!" />
													: <>
														<Trash2 size={16} />
														<span>  حذف</span>
													</>}
											</button>

											<div className="text-jmt-neutral-300">|</div>
										</div>
									)}

									{/* «سبد خرید بعدی» فعلا غیرفعال است
									<Button
										end_icon={<ChevronLeft size={16} />}
										color_type="tertiary"
										variant="tertiary"
										text=" بعدا میخرم "
									/>
									*/}
								</div>
							</div>

							{/* title */}
							<Link href={item?.product?.seo?.full_url || item?.product?.seo?.url || "#"}>
								<p className={`font-bold ${isAvailable ? 'text-jmt-main' : 'text-jmt-neutral-500 opacity-70'}`}>
									{item?.product?.title}
								</p>
							</Link>

							{/*mix variants*/}
							<div className={`flex items-center gap-2 text-xs mt-1  ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}>
								<div className="flex items-center gap-1">
									<div
										className="w-4 h-4 rounded-full border border-neutral-300"
										style={{
											background: `${check_product_availability(mix_variant?.[0]) ?
												item?.product?.mix_variant[0]?.labels_obj?.colors?.value?.color : '#737373'
												}`
										}}
									/>
									<p>رنگ</p>
									<p>{item?.product?.mix_variant[0]?.labels_obj?.colors?.title || 'نامشخص'}</p>
								</div>
								|
								<div className="flex items-center gap-1">
									<p>سایز</p>
									<p>{item?.product?.mix_variant[0]?.labels_obj?.sizes?.title || 'نامشخص'}</p>
								</div>
							</div>

							{/*original or fake product*/}
							<div className={`flex items-center gap-1 text-xs ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}>
								<ProductLabelGuarantee noneOrginal={item?.product?.custom_fields?.non_original_product} className="w-4.5! h-4.5!" textClassName="font-normal!" />
							</div>

							{/*shipping time - زمان آماده‌سازی (فقط پیش‌فروش) */}
							{is_presale && (
								<PresaleLeadTime
									hours={presale_row?.lead_hours}
									label="زمان آماده سازی محصول"
									icon={<Timer className="w-4.5 h-4.5 stroke-jmt-neutral-700" />}
									className={`text-xs ${!isAvailable ? "text-jmt-neutral-500 opacity-70" : "text-jmt-neutral-700 "}`}
								/>
							)}

						</div>

					</div>

					{/*price & counter & changes */}
					<div
						className={`flex items-end gap-4 ${hasChanges && isAvailable ? "justify-between" : "justify-end"
							}`}
					>
						{check_product_availability(mix_variant?.[0]) && hasChanges && (
							<div className="flex flex-col items-start gap-1">
								{/*count changes*/}
								{item?.count_history?.length > 0 &&
									item?.count_history?.map((count, index) => {
										const different_count =
											index === item?.count_history?.length - 1
												? item?.count - count?.old_count
												: item?.count_history[index + 1]?.old_count - count?.old_count;

										if (different_count < 0) {
											return (
												<div
													key={`count-change-${index}`}
													className="flex items-center gap-1 text-xs text-jmt-yellow-600"
												>
													<Info size={16} className="text-jmt-yellow-600 shrink-0" />
													<span>
														تغییر تعداد انتخاب شده به دلیل کاهش موجودی انبار
													</span>
												</div>
											);
										}

										return null;
									})}

								{/*price changes up*/}
								{item?.first_price > 0 &&
									mix_variant?.[0]?.price?.main_price > 0 &&
									mix_variant?.[0]?.price?.main_price > item?.first_price && (
										<div className="flex items-center gap-1 text-xs">
											<Info size={16} className="text-jmt-orange-600 shrink-0" />

											<div className="text-jmt-orange-600">
												<span>قیمت از</span>

												<PriceComponent
													price={item?.first_price}
													className="inline-block mx-px"
												/>

												<span>به </span>

												<PriceComponent
													price={mix_variant?.[0]?.price?.main_price}
													className="inline-block mx-px"
												/>

												<span>افزایش یافت</span>
											</div>
										</div>
									)}

								{/*price changes down*/}
								{item?.first_price > 0 &&
									mix_variant?.[0]?.price?.main_price > 0 &&
									mix_variant?.[0]?.price?.main_price < item?.first_price && (
										<div className="flex items-center gap-1 text-xs">
											<Info size={16} className="text-jmt-green-600 shrink-0" />

											<div className="text-jmt-green-600">
												<span>قیمت از</span>

												<PriceComponent
													price={item?.first_price}
													className="inline-block mx-px"
												/>

												<span>به </span>

												<PriceComponent
													price={mix_variant?.[0]?.price?.main_price}
													className="inline-block mx-px"
												/>

												<span>کاهش یافت</span>
											</div>
										</div>
									)}
							</div>
						)}

						{/*price & counter*/}
						<div className="flex items-center justify-end space-x-6">
							{/*price*/}
							{check_product_availability(mix_variant?.[0]) ? (
								<div className="flex flex-col items-end gap-1">
									{/* offer section */}
									{check_product_offer(mix_variant?.[0]) && (
										<div className="flex items-center gap-1">
											<PriceComponent
												price={mix_variant?.[0]?.price?.price * item?.count}
												className="text-jmt-neutral-400 line-through text-sm [&>.price-currency]:hidden"
											/>

											{mix_variant?.[0]?.price?.discount_percent && (
												<div className="flex items-center justify-center bg-jmt-red-700 rounded-md px-1 py-px">
													<span className="text-jmt-white text-xs font-bold">
														{mix_variant?.[0]?.price?.discount_percent}٪
													</span>
												</div>
											)}
										</div>
									)}

									{/* final price + remain product */}
									<div className="flex items-center space-x-6">
										<RemainProduct />

										<PriceComponent
											price={mix_variant?.[0]?.price?.main_price * item?.count}
											className="font-bold text-lg text-neutral-900"
										/>
									</div>
								</div>
							) : (
								<div className="font-bold text-jmt-neutral-500">
									ناموجود
								</div>
							)}

							{/*counter*/}
							{check_product_availability(mix_variant?.[0]) ? (
								<div className="[&>.counter-component>.link-to-cart]:hidden">
									<Counter
										min={mix_variant_limit_min({
											mix_variant: {
												...mix_variant?.[0],
												keys: item?.mix_variant_keys,
											},
										})}
										max={mix_variant_limit_max({
											mix_variant: {
												...mix_variant?.[0],
												keys: item?.mix_variant_keys,
											},
										})}
										removeHandler={delete_from_cart_handler}
										quantity={item_count_in_cart({
											mix_variant: {
												...mix_variant?.[0],
												keys: item?.mix_variant_keys,
											},
										})}
										onChange={change_count_handler}
										loading={add_to_cart_loading?.[item?.mix_variant_keys]}
										remove_from_cart_loading={
											remove_from_cart_loading?.[item?.mix_variant_keys]
										}
									/>
								</div>
							) : (
								<Button
									start_icon={
										is_notify?.[mix_variant?.[0]?.keys] ? (
											<BellOff size={20} />
										) : (
											<BellRing size={20} />
										)
									}
									text={
										is_notify?.[mix_variant?.[0]?.keys]
											? " دیگه لازم نیست خبرم کنید "
											: " موجود شد خبرم کنید "
									}
									onClick={(e) => add_to_notify(e)}
									color_type={
										is_notify?.[mix_variant?.[0]?.keys] ? "secondary" : "primary"
									}
									className="whitespace-nowrap w-full"
								/>
							)}
						</div>
					</div>

				</div>
			</div>
		</>
	)
}
export default CartItem

